import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/** Subset of Telegram user data exposed via context. */
interface TelegramUser {
  readonly id: number
  readonly firstName: string
  readonly lastName?: string
  readonly username?: string
  readonly languageCode?: string
  readonly isPremium?: boolean
}

/** Values exposed by TelegramProvider to the entire component tree. */
interface TelegramContextValue {
  /** Raw initData string for backend auth header. */
  readonly initData: string
  /** Parsed user object (null when running outside Telegram). */
  readonly user: TelegramUser | null
  /** Telegram color scheme — 'dark' | 'light'. */
  readonly colorScheme: 'dark' | 'light'
  /** Whether the app is running inside the Telegram WebApp context. */
  readonly isTelegram: boolean
}

const TelegramContext = createContext<TelegramContextValue | null>(null)

/**
 * Hook to access Telegram WebApp context.
 * Must be used within `<TelegramProvider>`.
 */
export function useTelegram(): TelegramContextValue {
  const ctx = useContext(TelegramContext)
  if (ctx === null) {
    throw new Error('useTelegram must be used within <TelegramProvider>')
  }
  return ctx
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface TelegramProviderProps {
  readonly children: ReactNode
}

/** Map raw Telegram user object (from window.Telegram.WebApp) to our camelCase interface. */
function mapUser(raw: { id: number; first_name: string; last_name?: string; username?: string; language_code?: string; is_premium?: boolean }): TelegramUser {
  return {
    id: raw.id,
    firstName: raw.first_name,
    lastName: raw.last_name,
    username: raw.username,
    languageCode: raw.language_code,
    isPremium: raw.is_premium,
  }
}

export function TelegramProvider({ children }: TelegramProviderProps) {
  const [isReady, setIsReady] = useState(false)

  const webApp = window.Telegram?.WebApp

  // Run SDK lifecycle calls exactly once on mount.
  useEffect(() => {
    if (webApp) {
      // Tell Telegram the app is ready so it hides the loading indicator.
      webApp.ready()

      // Expand to full viewport height.
      webApp.expand()

      // Adapt native header / background to the current theme.
      try {
        const bg = webApp.themeParams['bg_color']
        const headerBg = webApp.themeParams['header_bg_color'] ?? bg
        if (headerBg) webApp.setHeaderColor(headerBg)
        if (bg) webApp.setBackgroundColor(bg)
      } catch {
        // Some older Telegram clients may not support setHeaderColor.
      }

      // Prevent accidental closure (user can still close via explicit action).
      try {
        webApp.enableClosingConfirmation()
      } catch {
        // Not supported in all clients.
      }
    }

    setIsReady(true)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo<TelegramContextValue>(() => {
    if (!webApp) {
      // Running outside Telegram (dev mode / direct browser access).
      return {
        initData: '',
        user: null,
        colorScheme: 'dark',
        isTelegram: false,
      }
    }

    const rawUser = webApp.initDataUnsafe.user
    return {
      initData: webApp.initData,
      user: rawUser ? mapUser(rawUser) : null,
      colorScheme: webApp.colorScheme,
      isTelegram: true,
    }
  }, [webApp])

  if (!isReady) {
    return (
      <div className="flex h-screen items-center justify-center bg-tg-bg">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-t-[var(--tg-theme-button-color)]" />
          <span className="text-sm text-tg-hint">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <TelegramContext.Provider value={value}>
      {children}
    </TelegramContext.Provider>
  )
}
